import React from 'react';
import { ExpenseWithDetails, ExpenseSearchParams } from './types';
import { expensesApi } from './services/expenseApi';
import { 
  PagerBar, 
  SearchListBar,
  SearchImportBar,
  ImportResultModal,
  SearchCriterion,
  SearchableField,
  SortBar,
  SortOption,
  SortColumn,
  ListItemSection,
  LoadingBar,
  formatCurrency,
  formatDate,
  ListItemDisplayData
} from '../../../components/common';
import '../../../components/common/CRMList.css';

interface ExpenseListProps {
  expenses: ExpenseWithDetails[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  selectedExpenseId?: number;
  onSelectExpense: (expense: ExpenseWithDetails) => void;
  onDeleteExpense: (expense: ExpenseWithDetails) => void;
  onSearch: (searchParams: ExpenseSearchParams) => void;
  onCreateNew: () => void;
  onImportComplete?: () => void;
  globalLoading: boolean;
  loadingMessage: string;
}

const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  loading,
  error,
  pagination,
  selectedExpenseId,
  onSelectExpense,
  onDeleteExpense,
  onSearch,
  onCreateNew,
  onImportComplete,
  globalLoading,
  loadingMessage
}) => {
  // Search state for search and sort functionality
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedCriteria, setSelectedCriteria] = React.useState<SearchCriterion[]>([]);
  const [importLoading, setImportLoading] = React.useState(false);
  const [importResult, setImportResult] = React.useState<{
    processed: number;
    notProcessed: Array<{ row: number; data: string; reason: string }>;
  } | null>(null);
  const [currentSorts, setCurrentSorts] = React.useState<SortOption[]>([
    {
      field: 'expenseDate',
      direction: 'desc',
      label: 'Expense Date'
    }
  ]);
  
  // Searchable fields configuration for SearchListBar
  // Amount: Expenses.amount
  // Property Name: Properties.name (join on propertyId)
  // Description: Expenses.description
  const searchableFields: SearchableField[] = [
    { key: 'amount', label: 'Amount', type: 'number' },
    { key: 'propertyName', label: 'Property Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' }
  ];
  
  // Sort column configuration - must match server valid sort fields
  const sortColumns: SortColumn[] = [
    { field: 'vendorName', label: 'Vendor Name' },
    { field: 'expenseDate', label: 'Expense Date' },
    { field: 'amount', label: 'Expense Amount' },
    { field: 'taxTypeName', label: 'Tax Type' }
  ];
  
  // Handle search value changes (debounced)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch) {
        if (selectedCriteria.length > 0) {
          // Multiple field search (OR logic)
          onSearch({
            search: searchValue,
            fields: selectedCriteria.map(c => c.field),
            page: 1 // Reset to first page on search
          });
        } else {
          // Global search across all fields
          onSearch({
            search: searchValue,
            fields: undefined,
            page: 1 // Reset to first page on search
          });
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, selectedCriteria]);
  
  // Handle criteria changes - only update state, let useEffect handle search
  const handleCriteriaChange = React.useCallback((criteria: SearchCriterion[]) => {
    setSelectedCriteria(criteria);
  }, []);
  
  // Handle sort changes
  const handleSortsChange = React.useCallback((sorts: SortOption[]) => {
    setCurrentSorts(sorts);
    
    if (onSearch) {
      if (sorts.length > 0) {
        // Convert sorts to the new format
        const sortArray = sorts.map(sort => ({
          field: sort.field as 'expenseDate' | 'vendorName' | 'amount' | 'taxTypeName',
          direction: sort.direction as 'asc' | 'desc'
        }));
        
        onSearch({
          sorts: sortArray,
          page: 1 // Reset to first page when sorting changes
        });
      } else {
        // Clear sorts if none selected
        onSearch({
          sorts: undefined,
          page: 1
        });
      }
    }
  }, [onSearch]);

  // Unified display data function for ExpenseList
  const getDisplayData = (expense: ExpenseWithDetails): ListItemDisplayData => ({
    title: expense.vendorName || 'Unknown Vendor',
    category: expense.taxTypeName || 'No Category',
    fields: [
      {
        name: 'amount',
        value: formatCurrency(expense.amount),
        isPrimary: true
      },
      {
        name: 'date',
        value: formatDate(expense.expenseDate)
      },
      {
        name: 'property',
        value: expense.propertyName || 'No Property'
      },
      {
        name: 'contact',
        value: expense.contactName || 'No Contact'
      }
    ]
  });

  const downloadErrorCsv = React.useCallback((csvContent: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'expenses-import-errors.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const handleImport = React.useCallback(async (file: File) => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const result = await expensesApi.importCsv(file);
      setImportResult({
        processed: result.processed,
        notProcessed: result.notProcessed
      });

      if (result.errorCsv && result.notProcessed.length > 0) {
        downloadErrorCsv(result.errorCsv);
      }

      onImportComplete?.();
    } catch (err) {
      console.error('Expense import failed:', err);
      setImportResult({
        processed: 0,
        notProcessed: [{ row: 0, data: file.name, reason: err instanceof Error ? err.message : 'Import failed' }]
      });
    } finally {
      setImportLoading(false);
    }
  }, [downloadErrorCsv, onImportComplete]);

  const handleCloseImportResult = React.useCallback(() => {
    setImportResult(null);
  }, []);

  if (error) {
    return (
      <div className="list">
        <div className="list-header">
          <h3>Expenses ({pagination.total})</h3>
          <button className="app-btn app-btn-primary list-btn-add-item" onClick={onCreateNew} title="Add New Expense">
            +
          </button>
        </div>
        <div className="app-error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="list">
      <div className="list-header">
        <h3>Expenses ({pagination.total})</h3>
        <div className="list-header-actions">
          <SearchImportBar
            onImport={handleImport}
            loading={importLoading}
            title="Import expenses from CSV"
          />
          <button className="app-btn app-btn-primary list-btn-add-item" onClick={onCreateNew} title="Add New Expense">
            +
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <SearchListBar
        searchableFields={searchableFields}
        searchValue={searchValue}
        selectedCriteria={selectedCriteria}
        onSearchChange={setSearchValue}
        onCriteriaChange={handleCriteriaChange}
        placeholder="Search expenses..."
      />

      {/* Sort Bar */}
      <SortBar
        availableColumns={sortColumns}
        currentSorts={currentSorts}
        onSortsChange={handleSortsChange}
        defaultSort={{
          field: 'expenseDate',
          direction: 'desc',
          label: 'Expense Date'
        }}
      />

      {/* Expense List */}
      <ListItemSection
        items={expenses}
        selectedItemId={selectedExpenseId}
        onSelectItem={onSelectExpense}
        onDeleteItem={onDeleteExpense}
        deleteConfirmMessage={(expense) => 
          `Are you sure you want to delete the expense for ${expense.vendorName}?`
        }
        loading={false}
        loadingMessage=""
        emptyMessage="No expenses found. Try adjusting your search criteria."
        getDisplayData={getDisplayData}
      />

      {/* Bottom PagerBar */}
      <PagerBar
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        itemsPerPage={pagination.limit}
        onPageChange={(page) => onSearch({ page })}
        onItemsPerPageChange={(limit) => onSearch({ limit, page: 1 })}
      />
      
      <LoadingBar isLoading={globalLoading} message={loadingMessage} />

      {importResult && (
        <ImportResultModal
          isOpen={!!importResult}
          processed={importResult.processed}
          notProcessed={importResult.notProcessed}
          onClose={handleCloseImportResult}
        />
      )}
    </div>
  );
};

export default ExpenseList;
